import { useState, useEffect } from 'preact/hooks'
import { useViewMode } from './hooks/useViewMode'
import { GridGallery } from './components/Gallery/GridGallery'
import { FeedScroll } from './components/Gallery/FeedScroll'
import { Navigation } from './components/Layout/Navigation'
import { InstallPrompt } from './components/PWA/InstallPrompt'
import { UpdateNotification } from './components/PWA/UpdateNotification'
import { registerServiceWorker, updateServiceWorker, precacheImages } from './lib/serviceWorker'
import type { ImageManifest } from './types'

export function App() {
  const [manifest, setManifest] = useState<ImageManifest | null>(null)
  const [loading, setLoading] = useState(true)
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null)
  const [showUpdate, setShowUpdate] = useState(false)
  const { viewMode, setViewMode } = useViewMode()

  // Register Service Worker
  useEffect(() => {
    registerServiceWorker({
      onUpdate: (registration) => {
        setSwRegistration(registration)
        setShowUpdate(true)
      },
      onSuccess: (registration) => {
        console.log('PWA ready for offline use!')
        setSwRegistration(registration)
      },
      onError: (error) => {
        console.error('Service Worker error:', error)
      }
    })
  }, [])

  // Load manifest and precache images
  useEffect(() => {
    fetch('/data/manifest.json')
      .then(res => res.json())
      .then(data => {
        setManifest(data)
        setLoading(false)
        
        // Precache first 10 images for offline viewing
        if (data.images && data.images.length > 0) {
          const imageUrls = data.images.slice(0, 10).flatMap((img: any) => [
            img.chibi.url,
            img.original.url
          ])
          precacheImages(imageUrls)
        }
      })
      .catch(err => {
        console.error('Failed to load manifest:', err)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <div class="flex items-center justify-center min-h-screen bg-cream">
        <div class="text-2xl text-soft-blue font-bold animate-pulse">
          Loading BoltBuddies...
        </div>
      </div>
    )
  }

  if (!manifest) {
    return (
      <div class="flex items-center justify-center min-h-screen bg-cream">
        <div class="text-xl text-red-500">
          Failed to load images. Please try again.
        </div>
      </div>
    )
  }

  const handleUpdate = () => {
    if (swRegistration) {
      updateServiceWorker(swRegistration)
    }
  }

  return (
    <div class="min-h-screen bg-pattern flex flex-col">
      <header class="p-6 text-center flex-shrink-0 relative overflow-hidden">
        {/* Decorative background shapes */}
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
          <div class="absolute -top-10 -left-10 w-32 h-32 bg-soft-pink opacity-20 rounded-full blur-2xl"></div>
          <div class="absolute -top-10 -right-10 w-40 h-40 bg-soft-blue opacity-20 rounded-full blur-2xl"></div>
          <div class="absolute top-1/2 left-1/4 w-24 h-24 bg-mint-green opacity-15 rounded-full blur-xl"></div>
        </div>
        
        {/* Main header content */}
        <div class="relative z-10">
          {/* Animated bus emoji */}
          <div class="mb-3">
            <span class="text-4xl inline-block animate-bounce-slow">🔩</span>
            <span class="text-2xl inline-block animate-float mx-2" style="animation-delay: 0.2s">✨</span>
            <span class="text-2xl inline-block animate-float" style="animation-delay: 0.4s">🎨</span>
          </div>
          
          <h1 class="text-4xl md:text-5xl font-bold bg-gradient-to-r from-soft-blue via-soft-purple to-soft-pink bg-clip-text text-transparent mb-3 animate-gradient">
            BoltBuddies Art
          </h1>
          
          <p class="text-gray-600 text-lg">
            Adorable chibi renditions of bus parts and other workshop parts
          </p>
          
          {/* Decorative divider */}
          <div class="mt-8 flex justify-center items-center gap-2">
            <div class="h-px w-12 bg-gradient-to-r from-transparent to-soft-blue"></div>
            <span class="text-soft-blue text-xl">◆</span>
            <div class="h-px w-12 bg-gradient-to-l from-transparent to-soft-blue"></div>
          </div>
        </div>
      </header>

      <main class="flex-1 overflow-hidden">
        {viewMode === 'grid' && <GridGallery images={manifest.images} />}
        {viewMode === 'feed' && <FeedScroll images={manifest.images} />}
      </main>

      {/* Footer with Made with Love - positioned above navigation */}
      <footer class="text-center py-3 pb-16 text-xs text-soft-pink animate-pulse">
        ♥ Made with love ♥
      </footer>

      <Navigation currentView={viewMode} onViewChange={setViewMode} />
      
      {/* PWA Components */}
      <InstallPrompt />
      {showUpdate && <UpdateNotification onUpdate={handleUpdate} />}
    </div>
  )
}
